/**
 * The "Mentions" card in the Research browse grid — chunk-level text hits.
 * Distinct from Documents because rows render a snippet + page context
 * instead of filename / type.
 */
import { Hash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import type { ChunkHit } from '@/services/api/case-search'
import { HighlightedText } from './HighlightedText'

interface Props {
  caseId: string
  chunks: ChunkHit[]
  query?: string
}

export function MentionsCard({ caseId, chunks, query }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  if (chunks.length === 0) return null

  return (
    <Card className="group/card flex flex-col overflow-hidden p-0 transition-colors hover:border-border/80">
      <div className="flex items-center gap-3 border-b border-border/60 px-3.5 py-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Hash className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {t('research.mentions.label')}
        </span>
        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {chunks.length}
        </span>
      </div>
      <ul className="divide-y divide-border/40">
        {chunks.map(hit => (
          <li key={hit.chunk_id} className="cursor-pointer transition-colors hover:bg-muted/30">
            <button
              type="button"
              onClick={() => {
                if (!hit.document_id) return
                const page = hit.page_number ? `&page=${hit.page_number}` : ''
                navigate(`/cases/${caseId}/documents?doc=${hit.document_id}${page}`)
              }}
              className="flex w-full items-start gap-2.5 px-3.5 py-2 text-left"
            >
              <Hash className="mt-1 size-3 shrink-0 text-muted-foreground/70" />
              <div className="min-w-0 flex-1">
                {/* The match sits mid-snippet: at phone width that is often the
                    third line, or inside a token longer than the line, so two
                    unbroken lines would hide the highlighted hit. */}
                <div className="line-clamp-3 text-[12.5px] leading-snug text-foreground max-md:break-words md:line-clamp-2">
                  <HighlightedText text={hit.snippet} query={query} />
                </div>
                <div className="truncate text-[10.5px] text-muted-foreground/70">
                  {hit.document_name || '—'}
                  {hit.page_number ? ` · p. ${hit.page_number}` : ''}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
